import { useState, useEffect, useMemo } from "react";

/**
 * Calculador de crédito fiscal por producción audiovisual (España).
 * Marco legal: Art. 36 Ley 27/2014 del Impuesto sobre Sociedades (LIS).
 *
 * Tipos de deducción:
 *  - "nacional"     → producciones cinematográficas y series ESPAÑOLAS (Art. 36.1)
 *                     30% sobre el primer millón € de base + 25% sobre el resto
 *                     Tope: 20% del coste total (50% en Canarias)
 *  - "animacion"    → animación (Art. 36.1 con regla especial)
 *                     25% sobre toda la base, mismo tope
 *  - "canarias"     → rodaje en Canarias (RIC, Régimen Económico Fiscal)
 *                     mismas tasas 30/25 pero tope 50%
 *  - "extranjera"   → producciones extranjeras rodadas en España (Art. 36.2)
 *                     30% sobre el primer millón + 25% sobre el resto del gasto
 *                     realizado en territorio español. Tope 20M€. Mínimo de gasto 1M€.
 *
 * Base de deducción: incluye los costes técnicos y artísticos directamente relacionados
 * con la producción. NO incluye gastos de publicidad, distribución, ni los gastos
 * efectuados fuera del territorio español. La aproximación práctica para grabarte
 * es: (Cap.01 a Cap.10) excluyendo Cap.11 (gastos generales) y Cap.12 (publicidad).
 *
 * Persistencia: se guarda en `project.tax_credit` con shape:
 *   { type, base_amount, territory_spending, cap_pct, added_to_financing }
 */

const TYPE_LABEL = {
  nacional: "Nacional (Art. 36.1 LIS)",
  animacion: "Animación (Art. 36.1)",
  canarias: "Rodaje en Canarias (REF)",
  extranjera: "Producción extranjera en España (Art. 36.2)",
};

const TYPE_HINT = {
  nacional: "Producción cinematográfica o serie española. 30% primer millón + 25% resto. Tope 20% del coste.",
  animacion: "Producción de animación. 25% sobre toda la base. Tope 20% del coste.",
  canarias: "Rodaje en Canarias. Mismas tasas 30/25 pero TOPE 50% del coste.",
  extranjera: "Producción extranjera rodada en España. 30% / 25% sobre el GASTO REALIZADO EN ESPAÑA. Mínimo 1M€ de gasto en territorio español.",
};

function calc(type, base, territorySpending) {
  const b = Number(base || 0);
  const t = Number(territorySpending || 0);
  if (type === "extranjera") {
    // Use territory spending, NOT total base
    if (t < 1_000_000) {
      return {
        deduction: 0,
        cap_applied: 0,
        bracket1: 0,
        bracket2: 0,
        warning: "El gasto en territorio español debe ser ≥ 1.000.000 €.",
      };
    }
    const b1 = Math.min(t, 1_000_000) * 0.30;
    const b2 = Math.max(0, t - 1_000_000) * 0.25;
    let total = b1 + b2;
    let cap = 20_000_000;
    const warning = total > cap ? `Capado al máximo legal de 20 M€.` : "";
    total = Math.min(total, cap);
    return { deduction: total, cap_applied: cap, bracket1: b1, bracket2: b2, warning };
  }
  if (type === "animacion") {
    const deduction = b * 0.25;
    return { deduction, cap_applied: 0, bracket1: deduction, bracket2: 0, warning: "" };
  }
  // nacional y canarias usan la misma fórmula 30/25
  const b1 = Math.min(b, 1_000_000) * 0.30;
  const b2 = Math.max(0, b - 1_000_000) * 0.25;
  return { deduction: b1 + b2, cap_applied: 0, bracket1: b1, bracket2: b2, warning: "" };
}

function applyCap(deduction, total, type) {
  // Tope sobre el coste total de la producción
  if (type === "extranjera") return { capped: deduction, cap_pct: null };
  const capPct = type === "canarias" ? 0.50 : 0.20;
  const cap = total * capPct;
  return {
    capped: Math.min(deduction, cap),
    cap_pct: capPct,
    cap_amount: cap,
    cap_hit: deduction > cap,
  };
}

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    .format(Number(n) || 0);

export default function TaxCreditCalculator({ project, onUpdate, onAddToFinancing }) {
  const tc = project?.tax_credit || {};
  const grand = useMemo(() => {
    return (project?.items || []).reduce(
      (s, it) => s + (it.quantity || 0) * (it.unit_price || 0),
      0
    );
  }, [project?.items]);

  // Default base = chapters 01..10 (excludes 11 gastos generales y 12 publicidad)
  const defaultBase = useMemo(() => {
    return (project?.items || [])
      .filter((it) => {
        const ch = String(it.chapter || "");
        return ch && ch !== "11" && ch !== "12";
      })
      .reduce((s, it) => s + (it.quantity || 0) * (it.unit_price || 0), 0);
  }, [project?.items]);

  const [type, setType] = useState(tc.type || "nacional");
  const [manualBase, setManualBase] = useState(
    tc.base_amount != null ? tc.base_amount : null
  );
  const [territory, setTerritory] = useState(tc.territory_spending ?? 0);
  const useDefault = manualBase == null;
  const base = useDefault ? defaultBase : manualBase;

  const raw = calc(type, base, territory);
  const capInfo = applyCap(raw.deduction, grand, type);
  const finalDeduction = capInfo.capped;
  const pctOfTotal = grand > 0 ? (finalDeduction / grand) * 100 : 0;

  const persist = () => {
    onUpdate({
      type,
      base_amount: useDefault ? null : Number(base),
      territory_spending: type === "extranjera" ? Number(territory) : 0,
      calculated_amount: Math.round(finalDeduction),
    });
  };

  // Persist on changes (debounced via useEffect)
  useEffect(() => {
    const t = setTimeout(persist, 600);
    return () => clearTimeout(t);
  }, [type, manualBase, territory, persist]);

  const alreadyAdded = (project?.financing || []).some(
    (f) => f.id === "tax-credit" || (f.name || "").toLowerCase().includes("crédito fiscal")
  );

  return (
    <section
      data-testid="tax-credit-calculator"
      className="mt-8 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm"
    >
      <header className="border-b border-emerald-200/60 bg-emerald-100/40 px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-800">
              Crédito fiscal · Art. 36 LIS
            </p>
            <h3 className="mt-0.5 font-serif text-xl text-stone-900">
              Calculador de deducción fiscal
            </h3>
          </div>
          <p className="text-[11px] text-stone-500">
            Estimación orientativa · consulta con tu asesor fiscal antes de presentar
          </p>
        </div>
      </header>

      <div className="grid gap-5 px-5 py-5 lg:grid-cols-[1.1fr_1fr]">
        {/* Inputs */}
        <div className="space-y-4">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
              Tipo de producción
            </span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="tc-type"
              className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-700"
            >
              {Object.entries(TYPE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <p className="mt-1 text-[11.5px] italic text-stone-600">{TYPE_HINT[type]}</p>
          </label>

          {type !== "extranjera" ? (
            <>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                  Base de deducción
                </span>
                <div className="mt-1 flex gap-2">
                  <input
                    type="number"
                    step="100"
                    value={Math.round(base)}
                    onChange={(e) => setManualBase(Number(e.target.value))}
                    data-testid="tc-base"
                    className="flex-1 rounded-md border border-stone-300 bg-white px-3 py-2 text-right font-mono text-sm outline-none focus:border-emerald-700"
                  />
                  <button
                    type="button"
                    onClick={() => setManualBase(null)}
                    data-testid="tc-reset-base"
                    disabled={useDefault}
                    title="Volver a calcular automáticamente desde Cap.01–10"
                    className="rounded-md border border-stone-300 bg-white px-3 text-[11px] font-semibold uppercase tracking-widest text-stone-600 hover:border-emerald-600 hover:text-emerald-700 disabled:opacity-40"
                  >
                    ↻ Auto
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-stone-500">
                  Por defecto: <strong>{fmtEUR(defaultBase)}</strong> (Cap.01–10 · excluye Cap.11 generales y Cap.12 publicidad).
                  Puedes ajustarlo manualmente.
                </p>
              </label>
            </>
          ) : (
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                Gasto en territorio español
              </span>
              <input
                type="number"
                step="100"
                value={Math.round(territory)}
                onChange={(e) => setTerritory(Number(e.target.value))}
                data-testid="tc-territory"
                className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-right font-mono text-sm outline-none focus:border-emerald-700"
              />
              <p className="mt-1 text-[11px] text-stone-500">
                Mínimo legal: 1.000.000 € · Tope deducción: 20.000.000 €
              </p>
            </label>
          )}
        </div>

        {/* Result */}
        <div className="space-y-3 rounded-xl bg-stone-900 p-5 text-white" data-testid="tc-result">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-300">
            Crédito fiscal estimado
          </p>
          <p className="font-mono text-4xl font-bold tabular-nums text-emerald-400" data-testid="tc-amount">
            {fmtEUR(finalDeduction)}
          </p>
          <p className="text-[12px] text-white/70">
            <strong className="text-white">{pctOfTotal.toFixed(1)}%</strong> sobre coste total ({fmtEUR(grand)})
          </p>

          <div className="my-2 h-px bg-white/15" />

          <ul className="space-y-1.5 text-[12px]">
            {type !== "animacion" && (
              <>
                <li className="flex justify-between">
                  <span className="text-white/65">30% · primer millón</span>
                  <span className="font-mono tabular-nums">{fmtEUR(raw.bracket1)}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-white/65">25% · resto</span>
                  <span className="font-mono tabular-nums">{fmtEUR(raw.bracket2)}</span>
                </li>
              </>
            )}
            {type === "animacion" && (
              <li className="flex justify-between">
                <span className="text-white/65">25% · base completa</span>
                <span className="font-mono tabular-nums">{fmtEUR(raw.bracket1)}</span>
              </li>
            )}
            {capInfo.cap_hit && (
              <li className="rounded-md bg-amber-500/15 px-2 py-1.5 text-[11.5px] text-amber-200">
                ⚠ Tope {Math.round(capInfo.cap_pct * 100)}% del coste total aplicado ({fmtEUR(capInfo.cap_amount)})
              </li>
            )}
            {raw.warning && (
              <li className="rounded-md bg-red-500/20 px-2 py-1.5 text-[11.5px] text-red-200">
                ⚠ {raw.warning}
              </li>
            )}
          </ul>

          <button
            type="button"
            onClick={() => onAddToFinancing(Math.round(finalDeduction), TYPE_LABEL[type])}
            data-testid="tc-add-to-financing"
            disabled={finalDeduction <= 0 || alreadyAdded}
            className="mt-3 w-full rounded-full bg-emerald-500 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-900 transition hover:bg-emerald-400 disabled:opacity-40"
          >
            {alreadyAdded ? "✓ Ya añadido al plan" : "+ Añadir a financiación"}
          </button>
        </div>
      </div>

      <footer className="border-t border-emerald-200/60 bg-emerald-50/40 px-5 py-3 text-[11px] text-stone-600">
        Marco legal: Art. 36 Ley 27/2014 LIS · Real Decreto 634/2015 (Reglamento) · Régimen
        Económico Fiscal de Canarias (REF). Esta calculadora es una estimación contable orientativa;
        la deducción efectiva depende del informe del ICAA, certificados del MINCOTUR y del
        cumplimiento de los requisitos territoriales y laborales.
      </footer>
    </section>
  );
}
